import React, { useEffect, useState } from 'react';
import { getMe, updateEmail, updatePassword, updateProfile } from './api';
import { useTranslation } from '../i18n';

// In-app account screen for any user (owner / manager / crew). Edit display
// name (nickname), email, and password.
export default function Profile({ onProfileUpdated }) {
  const { t } = useTranslation();
  const [me, setMe] = useState(null);

  const [name, setName] = useState('');
  const [nameMsg, setNameMsg] = useState(null);
  const [savingName, setSavingName] = useState(false);

  const [emailForm, setEmailForm] = useState({ current_password: '', new_email: '' });
  const [emailMsg, setEmailMsg] = useState(null);
  const [savingEmail, setSavingEmail] = useState(false);

  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwMsg, setPwMsg] = useState(null);
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((res) => {
        if (cancelled) return;
        setMe(res.data);
        setName(res.data?.user?.user_metadata?.display_name || res.data?.member?.member_name || '');
      })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, []);

  const submitName = async (e) => {
    e.preventDefault();
    setNameMsg(null);
    setSavingName(true);
    try {
      const res = await updateProfile(name.trim());
      setNameMsg({ ok: true, text: t('profile.name_saved') });
      if (onProfileUpdated) onProfileUpdated({ display_name: res.data.display_name });
    } catch (err) {
      setNameMsg({ ok: false, text: err.response?.data?.error || t('profile.name_error') });
    }
    setSavingName(false);
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setEmailMsg(null);
    setSavingEmail(true);
    try {
      const res = await updateEmail(emailForm.current_password, emailForm.new_email);
      setEmailMsg({ ok: true, text: t('profile.email_saved').replace('{email}', res.data.email) });
      setEmailForm({ current_password: '', new_email: '' });
      setMe((m) => (m ? { ...m, user: { ...m.user, email: res.data.email } } : m));
    } catch (err) {
      setEmailMsg({ ok: false, text: err.response?.data?.error || t('profile.email_error') });
    }
    setSavingEmail(false);
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (pwForm.new_password !== pwForm.confirm) {
      setPwMsg({ ok: false, text: t('profile.pw_mismatch') });
      return;
    }
    setSavingPw(true);
    try {
      await updatePassword(pwForm.current_password, pwForm.new_password);
      setPwMsg({ ok: true, text: t('profile.pw_saved') });
      setPwForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      setPwMsg({ ok: false, text: err.response?.data?.error || t('profile.pw_error') });
    }
    setSavingPw(false);
  };

  const email = me?.user?.email || '—';
  const roleLabel = me?.role ? me.role.charAt(0).toUpperCase() + me.role.slice(1) : '';

  return (
    <div style={{ maxWidth: 560 }}>
      <form onSubmit={submitName} style={styles.card}>
        <div style={styles.cardTitle} id="profile-name-title">{t('profile.display_name')}</div>
        <div style={styles.cardSub} id="profile-name-hint">{t('profile.display_name_hint')}</div>
        {nameMsg && (
          <div
            role={nameMsg.ok ? 'status' : 'alert'}
            aria-live="polite"
            style={nameMsg.ok ? styles.ok : styles.err}
          >
            {nameMsg.text}
          </div>
        )}
        <div style={styles.field}>
          <label htmlFor="profile-display-name" style={styles.label}>{t('profile.display_name')}</label>
          <input
            style={styles.input}
            id="profile-display-name"
            value={name}
            maxLength={60}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('profile.display_name_placeholder')}
            aria-describedby="profile-name-hint"
          />
        </div>
        <button type="submit" disabled={savingName} style={styles.saveBtn}>
          {savingName ? t('app.opening_dots') : t('profile.save_name')}
        </button>
      </form>

      <div style={styles.card}>
        <div style={styles.cardTitle}>{t('profile.account')}</div>
        <div style={styles.infoRow}><span style={styles.infoLabel}>{t('login.work_email')}</span><span style={styles.infoValue}>{email}</span></div>
        {roleLabel && <div style={styles.infoRow}><span style={styles.infoLabel}>{t('profile.role')}</span><span style={styles.infoValue}>{roleLabel}</span></div>}
      </div>

      <form onSubmit={submitEmail} style={styles.card}>
        <div style={styles.cardTitle}>{t('profile.change_email')}</div>
        {emailMsg && (
          <div
            role={emailMsg.ok ? 'status' : 'alert'}
            aria-live="polite"
            style={emailMsg.ok ? styles.ok : styles.err}
          >
            {emailMsg.text}
          </div>
        )}
        <div style={styles.field}>
          <label htmlFor="profile-email-current-pw" style={styles.label}>
            {t('profile.current_password')} <span aria-hidden="true">*</span>
          </label>
          <input
            style={styles.input}
            id="profile-email-current-pw"
            type="password"
            required
            aria-required="true"
            autoComplete="current-password"
            value={emailForm.current_password}
            onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })}
          />
        </div>
        <div style={styles.field}>
          <label htmlFor="profile-new-email" style={styles.label}>
            {t('profile.new_email')} <span aria-hidden="true">*</span>
          </label>
          <input
            style={styles.input}
            id="profile-new-email"
            type="email"
            required
            aria-required="true"
            autoComplete="email"
            value={emailForm.new_email}
            onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
          />
        </div>
        <button type="submit" disabled={savingEmail} style={styles.saveBtn}>
          {savingEmail ? t('app.opening_dots') : t('profile.update_email')}
        </button>
      </form>

      <form onSubmit={submitPassword} style={styles.card}>
        <div style={styles.cardTitle}>{t('profile.change_password')}</div>
        {pwMsg && (
          <div
            role={pwMsg.ok ? 'status' : 'alert'}
            aria-live="polite"
            style={pwMsg.ok ? styles.ok : styles.err}
          >
            {pwMsg.text}
          </div>
        )}
        <div style={styles.field}>
          <label htmlFor="profile-pw-current" style={styles.label}>
            {t('profile.current_password')} <span aria-hidden="true">*</span>
          </label>
          <input
            style={styles.input}
            id="profile-pw-current"
            type="password"
            required
            aria-required="true"
            autoComplete="current-password"
            value={pwForm.current_password}
            onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
          />
        </div>
        <div style={styles.field}>
          <label htmlFor="profile-pw-new" style={styles.label}>
            {t('profile.new_password')} <span aria-hidden="true">*</span>
          </label>
          <input
            style={styles.input}
            id="profile-pw-new"
            type="password"
            required
            aria-required="true"
            minLength={8}
            autoComplete="new-password"
            value={pwForm.new_password}
            onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
          />
        </div>
        <div style={styles.field}>
          <label htmlFor="profile-pw-confirm" style={styles.label}>
            {t('profile.confirm_password')} <span aria-hidden="true">*</span>
          </label>
          <input
            style={styles.input}
            id="profile-pw-confirm"
            type="password"
            required
            aria-required="true"
            minLength={8}
            autoComplete="new-password"
            value={pwForm.confirm}
            onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
          />
        </div>
        <button type="submit" disabled={savingPw} style={styles.saveBtn}>
          {savingPw ? t('app.opening_dots') : t('profile.update_password')}
        </button>
      </form>
    </div>
  );
}

const styles = {
  card: { background: '#fff', border: '1px solid #e3e9df', borderRadius: 14, padding: 22, marginBottom: 16 },
  cardTitle: { fontFamily: 'Manrope, sans-serif', fontSize: 16, fontWeight: 800, color: '#1a1a1a', marginBottom: 6, letterSpacing: '-0.2px' },
  cardSub: { fontSize: 12.5, color: '#5f6b58', marginBottom: 14 },
  infoRow: { display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid #f0f2ec' },
  infoLabel: { fontSize: 13, color: '#5f6b58', fontWeight: 600 },
  infoValue: { fontSize: 13.5, color: '#1a1a1a', fontWeight: 600 },
  field: { display: 'flex', flexDirection: 'column', marginBottom: 12 },
  label: { fontSize: 12, fontWeight: 600, color: '#5f6b58', marginBottom: 6 },
  input: { width: '100%', padding: '11px 13px', border: '1px solid #e3e9df', borderRadius: 9, fontSize: 14, color: '#1a1a1a', background: '#f7f9f4', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' },
  saveBtn: { padding: '11px 18px', border: 'none', background: '#14653a', borderRadius: 9, fontSize: 14, fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: 'inherit' },
  ok: { background: '#ecf8ee', color: '#14653a', border: '1px solid #cfe9d4', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 14 },
  err: { background: '#FEF2F2', color: '#dc2626', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 14 }
};
